import { observer, Trigger, TriggerProps, useAdditionalNode, useExpressionScope, useStore, WatchChildStore } from '@yimoka/react';
import { isSuccess } from '@yimoka/shared';
import { IStore, refreshParentStore } from '@yimoka/store';
import { Alert, Drawer as AntDrawer, DrawerProps as AntDrawerProps, ButtonProps, Flex, FlexProps, Space } from 'antd';
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '../base/button';
import { useLocaleComponent } from '../hooks/use-locale';
import { usePropsValue } from '../hooks/use-props-value';
import { strToIcon } from '../tools/icon';

export type DrawerProps = AntDrawerProps & {
  trigger?: TriggerProps | false
  triggerText?: ReactNode
  onOpen?: TriggerProps['onTrig']

  /** 确定按钮点击后是否关闭弹窗
   * true: 总是关闭
   * 'success': 成功时关闭
   * 'fail': 失败时关闭
   */
  closeOnOk?: boolean | 'success' | 'fail'
  /** 是否绑定子 store */
  bindChildStore?: boolean
  /** 子 store 的回调函数 */
  onChildStore?: (store: IStore) => void
  /** 执行成功时 是否执行 store 的 fetch */
  isRefresh?: boolean
  parentStore?: IStore

  footer?: ReactNode | true
  okButtonProps?: ButtonProps
  cancelButtonProps?: ButtonProps
  footerProps?: FlexProps
  onOk?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void
}

/** Drawer 渲染可绑定子 Store 的抽屉，并统一处理提交、刷新与关闭时机。 */
export const Drawer = observer((props: DrawerProps) => {
  const {
    trigger,
    triggerText,
    onOpen,
    title,
    onClose,
    closeOnOk = 'success',
    closeIcon,
    footer,
    bindChildStore,
    onChildStore,
    children,
    parentStore,
    isRefresh,
    extra,
    footerProps,
    okButtonProps,
    cancelButtonProps,
    destroyOnHidden,
    onOk,
    ...rest
  } = props;
  const [childStore, setChildStore] = useState<IStore>();
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const pStore = useStore(parentStore);
  const local = useLocaleComponent('Common');

  const extraNode = useAdditionalNode('extra', extra);
  const footerNode = useAdditionalNode('footer', footer);
  const titleNode = useAdditionalNode('title', title);
  const scope = useExpressionScope();

  // 内部状态管理
  const [open, setOpen] = usePropsValue({
    value: props.open,
    defaultValue: false,
    onChange: (v) => {
      if (v === false) {
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        props.onClose?.({} as React.MouseEvent);
      }
    },
  });

  // 处理确定按钮点击事件
  const handleOk = useCallback((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    if (!bindChildStore || !childStore) {
      onOk?.(e);
      if (closeOnOk === true || closeOnOk === 'success') {
        setOpen(false);
      }
      return;
    }
    // Store 的 loading 要到请求开始后才更新；使用同步锁拦截同一事件循环内的双击，
    // 避免新增、编辑等写操作在服务端幂等键缺失时被重复提交。
    if (submittingRef.current) return;
    submittingRef.current = true;
    onOk?.(e);

    const { fetch, form } = childStore;

    /** run 串行执行表单校验、请求和父列表回读，并确保同步提交锁最终释放。 */
    const run = async () => {
      setSubmitting(true);
      try {
        if (form?.submit) await form.submit();
        const res = await fetch();
        if (isSuccess(res) && isRefresh) {
          await refreshParentStore(pStore, scope);
        }
        if (
          closeOnOk === true
          || (closeOnOk === 'success' && isSuccess(res))
          || (closeOnOk === 'fail' && !isSuccess(res))
        ) {
          setOpen(false);
        }
      } catch {
        // Form 校验错误由字段展示，Store 请求错误由其 notifier 展示；这里只阻止事件回调产生未处理 Promise。
      } finally {
        // 校验或请求失败后必须同时释放同步锁与可见提交态，保留用户修正表单并重试的能力。
        submittingRef.current = false;
        setSubmitting(false);
      }
    };
    void run();
  }, [onOk, bindChildStore, childStore, closeOnOk, setOpen, isRefresh, pStore, scope]);

  const { loading, form } = childStore ?? {};
  const disabled = form?.disabled || !!form?.errors?.length;
  const pending = submitting || Boolean(loading);

  // 处理确定按钮的属性
  const okProps = useMemo(() => {
    // onClick 处理抽屉操作按钮点击并转发原始事件。
    const onClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      handleOk(e);
      okButtonProps?.onClick?.(e);
    };
    const btnProps: ButtonProps = { type: 'primary', ...okButtonProps, onClick };
    if (!bindChildStore || !childStore) {
      return btnProps;
    }

    return { ...btnProps, disabled: disabled || submitting, loading: pending };
  }, [bindChildStore, childStore, disabled, handleOk, okButtonProps, pending, submitting]);

  const curFooter = useMemo(() => {
    if (footerNode === true) {
      return (
        <Flex wrap justify="end" {...footerProps}>
          {bindChildStore
            ? <Space wrap>
              <Button
                children={local.cancel}
                {...cancelButtonProps}
                disabled={submitting || cancelButtonProps?.disabled}
                onClick={() => {
                  if (!submittingRef.current) setOpen(false);
                }}
              />
              <Button children={local.confirm} {...okProps} />
            </Space>
            : <Button children={local.confirm} {...okProps} />}
        </Flex>
      );
    }
    return footerNode;
  }, [bindChildStore, cancelButtonProps, footerNode, footerProps, local.cancel, local.confirm, okProps, setOpen, submitting]);

  useEffect(() => {
    if (!open && destroyOnHidden) {
      // 关闭并销毁弹层时同步释放子 store 引用，保留既有行为。

      setChildStore(undefined);
    }
  }, [open, destroyOnHidden]);

  const curChildren = useMemo(() => (
    <Alert.ErrorBoundary>
      {bindChildStore ? (
        <WatchChildStore
          children={children}
          onStore={(store) => {
            setChildStore(store);
            props?.onChildStore?.(store);
          }}
        />
      ) : (
        children
      )}
    </Alert.ErrorBoundary>
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [bindChildStore, children]);

  const curTrigger = useMemo(() => {
    if (trigger !== false) {
      return <Trigger
        children={triggerText ?? titleNode}
        component={Button}
        {...trigger}
        onTrig={(...args) => {
          setOpen(true);
          trigger?.onTrig?.(...args);
          onOpen?.(...args);
        }}
      />;
    }
    return null;
  }, [onOpen, setOpen, titleNode, trigger, triggerText]);

  return (
    <>
      {curTrigger}
      <AntDrawer
        {...rest}
        children={curChildren}
        closeIcon={strToIcon(closeIcon)}
        destroyOnHidden={destroyOnHidden}
        extra={extraNode}
        footer={curFooter}
        open={open}
        title={titleNode}
        onClose={(e) => {
          // 提交中的关闭会让用户误以为请求已取消，并可能再次创建，因此等待本次请求完成。
          if (submittingRef.current) return;
          setOpen(false);
          onClose?.(e);
        }}
      />
    </>
  );
});
